// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data: uploading one file, encrypted in the browser
 * (docs/AYNI_DATA_INTAKE_API.md §4.2 and §12). No DOM: it runs in a Web
 * Worker (`ownDataUpload.worker.ts`) so hashing and sealing 64 MiB parts
 * never stall the globe, and in the tests as it is.
 *
 *   POST /api/data/uploads → the job, the part addresses and the dataset key
 *   for each part, in order: read → hash (the whole file, incrementally)
 *                            → seal (AYD1, kind 1, AAD naming the part)
 *                            → PUT, at most `concurrency` at a time
 *   POST /api/data/uploads/:job/complete { sha256, parts }
 *
 * **Memory** stays bounded: one plaintext part being read and at most
 * `concurrency` sealed parts in flight (about 200 MiB at 2).
 *
 * **Resuming.** Each stored part is written in the ledger with its ETag,
 * envelope hash and size. A dropped connection pauses the upload; Resume
 * re-reads the file (the plaintext hash has to cover every byte, in order)
 * but only seals and sends the parts not yet stored. Part addresses last
 * an hour: they are asked for again, for the missing parts only, when they
 * are about to expire or R2 refuses one. A page reload cannot resume:
 * the dataset key is handed out once and kept in memory only, so the
 * abandoned upload is shredded by the server after 24 hours.
 *
 * **The key** lives in this module's caller's memory for the upload only;
 * `forgetKey` overwrites it. It is never logged, stored or posted anywhere
 * but to `sealEnvelope`.
 */

import { sha256 } from '@noble/hashes/sha2.js'
import { ENVELOPE_KIND, ENVELOPE_OVERHEAD, fromBase64Url, hex, partAad, sealEnvelope } from './ownDataCrypto'
import { OWN_DATA_PART_BYTES } from './ownData'

/** Parts sent at once. Two keeps memory near 200 MiB and a home uplink busy. */
export const UPLOAD_CONCURRENCY = 2
/** Ask for new part addresses this long before the old ones expire. */
export const URL_REFRESH_MARGIN_MS = 5 * 60 * 1000
/** Tries per part before the upload pauses. */
export const PART_ATTEMPTS = 4

/** The plaintext byte range `[start, end)` of part `n` (1-based). */
export function partRange(n: number, fileBytes: number, partBytes = OWN_DATA_PART_BYTES): { start: number; end: number } {
  const start = (n - 1) * partBytes
  return { start, end: Math.min(fileBytes, start + partBytes) }
}

/** What `POST /api/data/uploads` answers (201). */
export interface UploadSession {
  job: string
  dataset: string
  tenant: string
  partBytes: number
  partCount: number
  /** Each part's address; `bytes` (the sealed size) when the site states it: the address signs that exact length. */
  parts: Array<{ n: number; url: string; bytes?: number }>
  expiresAt: number
  key: { alg?: string; envelope?: string; id: string; k: string }
}

/** One stored part, as `complete` wants it. */
export interface StoredPart {
  n: number
  etag: string
  sha256: string
  bytes: number
}

/** Which parts are stored, and where the others go. */
export class PartLedger {
  readonly partCount: number
  private readonly urls = new Map<number, string>()
  private readonly sizes = new Map<number, number>()
  private readonly stored = new Map<number, StoredPart>()
  private expiresAtMs = 0

  constructor(partCount: number) {
    this.partCount = partCount
  }

  setUrls(parts: ReadonlyArray<{ n: number; url: string; bytes?: number }>, expiresAt: number): void {
    for (const p of parts) {
      if (!Number.isInteger(p.n) || p.n < 1 || p.n > this.partCount) continue
      this.urls.set(p.n, p.url)
      if (typeof p.bytes === 'number') this.sizes.set(p.n, p.bytes)
    }
    this.expiresAtMs = expiresAt
  }

  /** The sealed size the site expects for part `n`, when it said. */
  expectedBytes(n: number): number | null {
    return this.sizes.get(n) ?? null
  }

  urlFor(n: number): string | null {
    return this.urls.get(n) ?? null
  }

  /** Whether the addresses are about to expire (or some missing part has none). */
  needsFreshUrls(nowMs: number, marginMs = URL_REFRESH_MARGIN_MS): boolean {
    if (nowMs >= this.expiresAtMs - marginMs) return true
    return this.missing().some((n) => !this.urls.has(n))
  }

  /** Forget a part's address (R2 refused it): the next refresh replaces it. */
  dropUrl(n: number): void {
    this.urls.delete(n)
  }

  markStored(part: StoredPart): void {
    this.stored.set(part.n, part)
  }

  isStored(n: number): boolean {
    return this.stored.has(n)
  }

  /** Part numbers not stored yet, in order. */
  missing(): number[] {
    const out: number[] = []
    for (let n = 1; n <= this.partCount; n++) if (!this.stored.has(n)) out.push(n)
    return out
  }

  storedCount(): number {
    return this.stored.size
  }

  /** Plaintext bytes stored so far. */
  storedPlainBytes(): number {
    let sum = 0
    for (const p of this.stored.values()) sum += p.bytes - ENVELOPE_OVERHEAD
    return sum
  }

  /** Every part, in order, for `complete`; throws while any is missing. */
  completeParts(): StoredPart[] {
    const missing = this.missing()
    if (missing.length) throw new UploadError('parts_incomplete', `parts not stored: ${missing.slice(0, 5).join(', ')}`)
    return [...this.stored.values()].sort((a, b) => a.n - b.n)
  }
}

/** Why an upload stopped. `resumable` errors pause it; the others end it. */
export class UploadError extends Error {
  readonly code: string
  readonly resumable: boolean
  readonly status: number | null
  constructor(code: string, message = code, opts: { resumable?: boolean; status?: number | null } = {}) {
    super(message)
    this.name = 'UploadError'
    this.code = code
    this.resumable = opts.resumable ?? false
    this.status = opts.status ?? null
  }
}

export interface UploadProgress {
  phase: 'starting' | 'sending' | 'completing'
  /** Plaintext bytes read and hashed. */
  readBytes: number
  /** Plaintext bytes R2 has confirmed. */
  sentBytes: number
  totalBytes: number
  partsStored: number
  partCount: number
}

/** Sends one sealed part; answers its ETag. Throws `UploadError` (with `status` for an HTTP answer). */
export type PutPart = (url: string, body: Uint8Array, onSent: (bytes: number) => void, signal?: AbortSignal) => Promise<string>

export interface UploadDeps {
  /** Same-origin calls to the site (the session cookie goes with them). */
  fetch: typeof fetch
  putPart: PutPart
  now: () => number
  sleep: (ms: number) => Promise<void>
  signal?: AbortSignal
  onProgress?: (p: UploadProgress) => void
  concurrency?: number
}

function aborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new UploadError('cancelled', 'cancelled')
}

async function readJson(res: Response): Promise<Record<string, unknown>> {
  return (await res.json().catch(() => ({}))) as Record<string, unknown>
}

/** A site call that failed, as an `UploadError`: network trouble and 5xx/429 pause, refusals end. */
async function siteError(res: Response | null, fallback: string): Promise<UploadError> {
  if (!res) return new UploadError('network', fallback, { resumable: true })
  const body = await readJson(res)
  const code = typeof body.error === 'string' ? body.error : fallback
  const resumable = res.status >= 500 || res.status === 429
  return new UploadError(code, code, { resumable, status: res.status })
}

async function post(deps: UploadDeps, path: string, body: unknown): Promise<Response | null> {
  try {
    return await deps.fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      signal: deps.signal,
    })
  } catch (err) {
    if (deps.signal?.aborted) throw new UploadError('cancelled')
    void err
    return null
  }
}

/** Ask the site for an upload session. */
export async function startUploadSession(deps: UploadDeps, file: { name: string; size: number }, title?: string): Promise<UploadSession> {
  const res = await post(deps, '/api/data/uploads', { fileName: file.name, bytes: file.size, ...(title?.trim() ? { title: title.trim() } : {}) })
  if (!res || res.status !== 201) throw await siteError(res, 'upload_refused')
  const s = (await readJson(res)) as unknown as UploadSession
  if (typeof s.job !== 'string' || typeof s.key?.k !== 'string' || !Array.isArray(s.parts) || !Number.isInteger(s.partCount)) {
    throw new UploadError('bad_answer')
  }
  return s
}

/** New addresses for the parts still missing. */
async function refreshUrls(deps: UploadDeps, session: UploadSession, ledger: PartLedger): Promise<void> {
  const missing = ledger.missing()
  if (!missing.length) return
  const res = await post(deps, `/api/data/uploads/${encodeURIComponent(session.job)}/parts`, { parts: missing })
  if (!res || res.status !== 200) throw await siteError(res, 'parts_refused')
  const body = (await readJson(res)) as { parts?: Array<{ n: number; url: string; bytes?: number }>; expiresAt?: number }
  if (!Array.isArray(body.parts) || typeof body.expiresAt !== 'number') throw new UploadError('bad_answer')
  ledger.setUrls(body.parts, body.expiresAt)
}

/** SHA-256 of bytes, as hex (WebCrypto: off the thread, one shot). */
async function digestHex(bytes: Uint8Array): Promise<string> {
  return hex(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>)))
}

/**
 * Seal and send every part not yet stored, hashing the whole file on the
 * way, in order. Answers the plaintext file's SHA-256 (hex). Throws an
 * `UploadError`; a `resumable` one leaves the ledger ready for another
 * call with the same key.
 */
export async function sendParts(file: Blob, session: UploadSession, ledger: PartLedger, key: Uint8Array, deps: UploadDeps): Promise<string> {
  const hasher = sha256.create()
  const concurrency = Math.max(1, deps.concurrency ?? UPLOAD_CONCURRENCY)
  const total = file.size
  let readBytes = 0
  const inFlightSent = new Map<number, number>()
  const report = () => {
    let partial = 0
    for (const v of inFlightSent.values()) partial += v
    deps.onProgress?.({
      phase: 'sending',
      readBytes,
      sentBytes: Math.min(total, ledger.storedPlainBytes() + partial),
      totalBytes: total,
      partsStored: ledger.storedCount(),
      partCount: ledger.partCount,
    })
  }
  const inflight = new Set<Promise<void>>()
  let failure: unknown = null

  const sendOne = async (n: number, sealed: Uint8Array, plainBytes: number): Promise<void> => {
    // The address signs the part's exact length: a part of another size would be refused, and means our slicing is wrong.
    const expected = ledger.expectedBytes(n)
    if (expected !== null && expected !== sealed.length) throw new UploadError('wrong_size', `part ${n} is ${sealed.length} bytes, expected ${expected}`)
    const envelopeSha = await digestHex(sealed)
    let lastError: UploadError | null = null
    for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt++) {
      aborted(deps.signal)
      if (ledger.needsFreshUrls(deps.now())) await refreshUrls(deps, session, ledger)
      const url = ledger.urlFor(n)
      if (!url) throw new UploadError('no_address', `no address for part ${n}`, { resumable: true })
      try {
        const etag = await deps.putPart(url, sealed, (b) => {
          // Envelope bytes sent, counted as plaintext for the bar.
          inFlightSent.set(n, Math.min(plainBytes, b))
          report()
        }, deps.signal)
        inFlightSent.delete(n)
        ledger.markStored({ n, etag, sha256: envelopeSha, bytes: sealed.length })
        report()
        return
      } catch (err) {
        inFlightSent.delete(n)
        if (deps.signal?.aborted) throw new UploadError('cancelled')
        const e = err instanceof UploadError ? err : new UploadError('network', String(err), { resumable: true })
        lastError = e
        // An expired or refused address: ask for a new one before the next try.
        if (e.status === 403 || e.status === 400) ledger.dropUrl(n)
        else if (e.status !== null && e.status < 500 && e.status !== 408 && e.status !== 429) throw e
        if (attempt < PART_ATTEMPTS) await deps.sleep(1000 * 2 ** (attempt - 1))
      }
    }
    throw new UploadError(lastError?.code ?? 'network', lastError?.message ?? 'part failed', { resumable: true, status: lastError?.status ?? null })
  }

  for (let n = 1; n <= ledger.partCount; n++) {
    aborted(deps.signal)
    if (failure) break
    const { start, end } = partRange(n, total, session.partBytes || OWN_DATA_PART_BYTES)
    const plain = new Uint8Array(await file.slice(start, end).arrayBuffer())
    hasher.update(plain)
    readBytes = end
    if (ledger.isStored(n)) {
      report()
      continue
    }
    const sealed = await sealEnvelope(key, ENVELOPE_KIND.part, partAad(session.tenant, session.dataset, session.job, n, ledger.partCount), plain)
    while (inflight.size >= concurrency) await Promise.race(inflight)
    if (failure) break
    const task: Promise<void> = sendOne(n, sealed, end - start)
      .catch((err) => {
        failure ??= err
      })
      .finally(() => {
        inflight.delete(task)
      })
    inflight.add(task)
    report()
  }
  await Promise.all(inflight)
  if (failure) throw failure instanceof UploadError ? failure : new UploadError('network', String(failure), { resumable: true })
  aborted(deps.signal)
  return hex(hasher.digest())
}

/** Tell the site every part is stored; answers whether the job went to the processor at once. */
export async function completeUpload(deps: UploadDeps, session: UploadSession, ledger: PartLedger, fileSha256: string): Promise<{ dispatched: boolean }> {
  const res = await post(deps, `/api/data/uploads/${encodeURIComponent(session.job)}/complete`, { sha256: fileSha256, parts: ledger.completeParts() })
  if (!res || res.status !== 200) throw await siteError(res, 'complete_refused')
  const body = await readJson(res)
  return { dispatched: body.dispatched === true }
}

/** The dataset key's bytes from the session (base64url), or an error. */
export function sessionKey(session: UploadSession): Uint8Array {
  const k = fromBase64Url(session.key.k)
  if (!k || k.length !== 32) throw new UploadError('bad_key')
  return k
}

/** Overwrite the key's bytes once the upload no longer needs them. */
export function forgetKey(key: Uint8Array | null): void {
  key?.fill(0)
}

/**
 * The default `putPart`: XMLHttpRequest where there is one (it reports
 * bytes as they leave, which `fetch` cannot), else `fetch`. Sends the
 * envelope with no other header and no cookie, as R2's presigned address
 * expects; answers the ETag R2 sends back.
 */
export const defaultPutPart: PutPart = (url, body, onSent, signal) => {
  if (typeof XMLHttpRequest === 'undefined') {
    return fetch(url, { method: 'PUT', body: body as Uint8Array<ArrayBuffer>, signal, credentials: sameOrigin(url) ? 'same-origin' : 'omit' })
      .then((res) => {
        if (!res.ok) throw new UploadError('part_refused', `part answered ${res.status}`, { status: res.status, resumable: res.status >= 500 })
        onSent(body.length)
        return res.headers.get('ETag') ?? ''
      }, (err) => {
        throw new UploadError('network', String(err), { resumable: true })
      })
  }
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.withCredentials = false
    xhr.upload.onprogress = (e) => onSent(e.loaded)
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag') ?? '')
      else reject(new UploadError('part_refused', `part answered ${xhr.status}`, { status: xhr.status, resumable: xhr.status >= 500 }))
    }
    xhr.onerror = () => reject(new UploadError('network', 'network error', { resumable: true }))
    xhr.ontimeout = xhr.onerror
    xhr.onabort = () => reject(new UploadError('cancelled'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(body as Uint8Array<ArrayBuffer>)
  })
}

function sameOrigin(url: string): boolean {
  try {
    return typeof location !== 'undefined' && new URL(url, location.href).origin === location.origin
  } catch {
    return false
  }
}

// ------------------------------------------------------------- the task

/** What an upload task tells whoever started it (the worker posts these). */
export type UploadEvent =
  | { type: 'session'; job: string; dataset: string; partCount: number; bytes: number; fileName: string }
  | { type: 'progress'; progress: UploadProgress }
  | { type: 'paused'; code: string; status: number | null }
  | { type: 'done'; job: string; dataset: string; dispatched: boolean }
  | { type: 'failed'; code: string; status: number | null; job: string | null; dataset: string | null }
  | { type: 'cancelled'; job: string | null; dataset: string | null }

/** What starts, resumes or stops a task. */
export type UploadCommand =
  | { type: 'start'; file: Blob & { name: string }; title?: string }
  | { type: 'resume' }
  | { type: 'cancel' }

/**
 * One upload from start to `complete`, driven by commands and reporting
 * events. Holds the dataset key between a pause and Resume, and forgets
 * it when the upload ends in any way.
 */
export class UploadTask {
  private file: (Blob & { name: string }) | null = null
  private session: UploadSession | null = null
  private ledger: PartLedger | null = null
  private key: Uint8Array | null = null
  private abort: AbortController | null = null
  private running = false
  private finished = false

  constructor(
    private readonly emit: (e: UploadEvent) => void,
    private readonly deps: Omit<UploadDeps, 'signal' | 'onProgress'>,
  ) {}

  handle(cmd: UploadCommand): void {
    if (cmd.type === 'start') void this.start(cmd.file, cmd.title)
    else if (cmd.type === 'resume') void this.run()
    else this.cancel()
  }

  private ids(): { job: string | null; dataset: string | null } {
    return { job: this.session?.job ?? null, dataset: this.session?.dataset ?? null }
  }

  private end(): void {
    this.finished = true
    forgetKey(this.key)
    this.key = null
    this.file = null
  }

  private async start(file: Blob & { name: string }, title?: string): Promise<void> {
    if (this.running || this.session || this.finished) return
    this.running = true
    this.abort = new AbortController()
    const deps = { ...this.deps, signal: this.abort.signal }
    try {
      this.emit({ type: 'progress', progress: { phase: 'starting', readBytes: 0, sentBytes: 0, totalBytes: file.size, partsStored: 0, partCount: 0 } })
      const session = await startUploadSession(deps, file, title)
      this.file = file
      this.session = session
      this.key = sessionKey(session)
      this.ledger = new PartLedger(session.partCount)
      this.ledger.setUrls(session.parts, session.expiresAt)
      this.emit({ type: 'session', job: session.job, dataset: session.dataset, partCount: session.partCount, bytes: file.size, fileName: file.name })
    } catch (err) {
      this.running = false
      const e = err instanceof UploadError ? err : new UploadError('network', String(err), { resumable: false })
      if (e.code === 'cancelled') this.emit({ type: 'cancelled', ...this.ids() })
      else this.emit({ type: 'failed', code: e.code, status: e.status, ...this.ids() })
      this.end()
      return
    }
    this.running = false
    await this.run()
  }

  private async run(): Promise<void> {
    if (this.running || this.finished || !this.session || !this.ledger || !this.file || !this.key) return
    this.running = true
    this.abort = new AbortController()
    const session = this.session
    const ledger = this.ledger
    const deps: UploadDeps = { ...this.deps, signal: this.abort.signal, onProgress: (progress) => this.emit({ type: 'progress', progress }) }
    try {
      const sha = await sendParts(this.file, session, ledger, this.key, deps)
      this.emit({ type: 'progress', progress: { phase: 'completing', readBytes: this.file.size, sentBytes: this.file.size, totalBytes: this.file.size, partsStored: ledger.partCount, partCount: ledger.partCount } })
      const { dispatched } = await completeUpload(deps, session, ledger, sha)
      this.emit({ type: 'done', job: session.job, dataset: session.dataset, dispatched })
      this.end()
    } catch (err) {
      const e = err instanceof UploadError ? err : new UploadError('network', String(err), { resumable: true })
      if (e.code === 'cancelled') {
        this.emit({ type: 'cancelled', ...this.ids() })
        this.end()
      } else if (e.resumable) {
        this.emit({ type: 'paused', code: e.code, status: e.status })
      } else {
        this.emit({ type: 'failed', code: e.code, status: e.status, ...this.ids() })
        this.end()
      }
    } finally {
      this.running = false
    }
  }

  private cancel(): void {
    if (this.finished) return
    if (this.running) {
      this.abort?.abort()
      return
    }
    this.emit({ type: 'cancelled', ...this.ids() })
    this.end()
  }
}
